import { eq } from "drizzle-orm";
import { db } from "../../db/index";
import { companies } from "../../db/schema/tenancy";

/**
 * Shared leave-year math (LeaveType::byUser L91–155 + LeavesQuotaRenew/AnnualReimburse window
 * logic). Kept dependency-light so the cron scripts can import it without pulling in Express.
 */

export const pad = (n: number) => String(n).padStart(2, "0");

export const companyTodayStr = (tz: string) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

export type CompanySettings = { id: number; leavesStartFrom: string; yearStartsFrom: string; timezone: string };

export async function companySettings(companyId: number): Promise<CompanySettings> {
  const row = await db
    .select({
      id: companies.id,
      leavesStartFrom: companies.leavesStartFrom,
      yearStartsFrom: companies.yearStartsFrom,
      timezone: companies.timezone,
    })
    .from(companies)
    .where(eq(companies.id, companyId))
    .limit(1);
  return {
    id: companyId,
    leavesStartFrom: row[0]?.leavesStartFrom ?? "year_start",
    yearStartsFrom: row[0]?.yearStartsFrom ?? "1",
    timezone: row[0]?.timezone ?? "Asia/Kolkata",
  };
}

/**
 * The active leave-year window [from, to) for a user. joining_date mode → from the joining
 * anniversary that has already passed; else the company financial/calendar year start.
 */
export function leaveWindow(
  s: { leavesStartFrom: string; yearStartsFrom: string },
  joiningDate: Date | string | null,
  todayStr: string,
): { from: string; to: string } {
  const p = todayStr.split("-");
  const ty = Number(p[0]), tm = Number(p[1]), td = Number(p[2]);
  const todayUTC = Date.UTC(ty, tm - 1, td);
  if (s.leavesStartFrom === "joining_date" && joiningDate) {
    const jd = joiningDate instanceof Date ? joiningDate : new Date(joiningDate);
    const jm = jd.getUTCMonth();
    const jdd = jd.getUTCDate();
    let startYear = ty;
    if (Date.UTC(ty, jm, jdd) > todayUTC) startYear = ty - 1;
    return { from: `${startYear}-${pad(jm + 1)}-${pad(jdd)}`, to: `${startYear + 1}-${pad(jm + 1)}-${pad(jdd)}` };
  }
  const mo = Math.min(12, Math.max(1, parseInt(s.yearStartsFrom || "1", 10) || 1));
  let startYear = ty;
  if (Date.UTC(ty, mo - 1, 1) > todayUTC) startYear = ty - 1;
  return { from: `${startYear}-${pad(mo)}-01`, to: `${startYear + 1}-${pad(mo)}-01` };
}

/** The PRIOR (just-ended) leave-year window — the year before the active one. Used by reset/CF. */
export function priorLeaveWindow(
  s: { leavesStartFrom: string; yearStartsFrom: string },
  joiningDate: Date | string | null,
  todayStr: string,
): { from: string; to: string } {
  const active = leaveWindow(s, joiningDate, todayStr);
  const shift = (iso: string) => {
    const p = iso.split("-");
    return `${Number(p[0]) - 1}-${p[1]}-${p[2]}`;
  };
  return { from: shift(active.from), to: shift(active.to) };
}

/** YYYY-MM-DD of the company leave-year anchor for "is today the renewal date?" checks. */
export function isYearStartToday(s: { yearStartsFrom: string }, todayStr: string): boolean {
  const mo = Math.min(12, Math.max(1, parseInt(s.yearStartsFrom || "1", 10) || 1));
  const [, m, d] = todayStr.split("-").map(Number);
  return m === mo && d === 1;
}

export function isJoiningAnniversaryToday(joiningDate: Date | string | null, todayStr: string): boolean {
  if (!joiningDate) return false;
  const jd = joiningDate instanceof Date ? joiningDate : new Date(joiningDate);
  const [, m, d] = todayStr.split("-").map(Number);
  return jd.getUTCMonth() + 1 === m && jd.getUTCDate() === d;
}
